/**
 * Workspace Attribution Table
 * Feature: 030-capacity-metrics (T070)
 * Constitution Version: 1.11.0
 * User Story: US4 - Workspace Capacity Attribution
 *
 * Sortable table displaying workspace-level storage and compute
 * attribution for a specific capacity.
 */

import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { Card, Badge, AppTooltip } from '../ui';
import { WorkspaceAttributionRow } from '@shared/types/capacityMetrics';
import { DualScrollTable, usePagination, PaginationControls } from '../common/TableControls';
import { formatEasternDate } from '@/utils/dateFormatter';
import { UnmeasuredSizeTag } from '@/utils/unmeasuredSize';

interface WorkspaceAttributionTableProps {
  data: WorkspaceAttributionRow[];
  capacityName?: string;
}

type SortField = 'workspace_name' | 'client_name' | 'storage_gb' | 'storage_percent' | 'lakehouse_count' | 'warehouse_count' | 'last_activity_date';
type SortOrder = 'asc' | 'desc';

/**
 * Workspace Attribution Table Component
 * Displays workspace storage breakdown with sorting (FR-003, SC-006)
 * Performance target: <3 seconds for 100+ workspaces
 */
export const WorkspaceAttributionTable: React.FC<WorkspaceAttributionTableProps> = ({
  data,
  capacityName,
}) => {
  const [sortField, setSortField] = useState<SortField>('storage_gb');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Handle sort click
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!data) return [];

    return [...data].sort((a, b) => {
      let aValue: string | number | Date | undefined;
      let bValue: string | number | Date | undefined;

      switch (sortField) {
        case 'workspace_name':
          aValue = a.workspace_name.toLowerCase();
          bValue = b.workspace_name.toLowerCase();
          break;
        case 'client_name':
          aValue = (a.client_name || '').toLowerCase();
          bValue = (b.client_name || '').toLowerCase();
          break;
        case 'storage_gb':
          aValue = a.storage_gb;
          bValue = b.storage_gb;
          break;
        case 'storage_percent':
          aValue = a.storage_percent;
          bValue = b.storage_percent;
          break;
        case 'lakehouse_count':
          aValue = a.lakehouse_count;
          bValue = b.lakehouse_count;
          break;
        case 'warehouse_count':
          aValue = a.warehouse_count;
          bValue = b.warehouse_count;
          break;
        case 'last_activity_date':
          aValue = a.last_activity_date ? new Date(a.last_activity_date).getTime() : 0;
          bValue = b.last_activity_date ? new Date(b.last_activity_date).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue === bValue) return 0;
      if (aValue === undefined || aValue === null || aValue === '') return 1;
      if (bValue === undefined || bValue === null || bValue === '') return -1;

      const comparison = aValue < bValue ? -1 : 1;
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [data, sortField, sortOrder]);

  // Calculate totals
  const totals = useMemo(() => {
    if (!data) return { totalStorage: 0, totalNotMeasured: 0, totalLakehouses: 0, totalWarehouses: 0 };
    return {
      totalStorage: data.reduce((sum, w) => sum + w.storage_gb, 0),
      // Spec 286 T159 item 32: the items totalStorage counts at their last known size (not measured).
      totalNotMeasured: data.reduce((sum, w) => sum + (Number(w.sizeNotMeasuredCount) || 0), 0),
      totalLakehouses: data.reduce((sum, w) => sum + w.lakehouse_count, 0),
      totalWarehouses: data.reduce((sum, w) => sum + w.warehouse_count, 0),
    };
  }, [data]);

  // Pagination over the sorted list
  const { page, setPage, pageSize, changeSize, total, totalPages, pageItems, start } =
    usePagination(sortedData);

  // Format date
  const formatDate = (date?: Date): string => {
    if (!date) return 'Never';
    const d = new Date(date);
    return formatEasternDate(d);
  };

  // Format storage size
  const formatStorage = (gb: number): string => {
    if (gb >= 1024) {
      return `${(gb / 1024).toFixed(2)} TB`;
    }
    return `${gb.toFixed(2)} GB`;
  };

  // Sortable column header
  const SortHeader: React.FC<{
    field: SortField;
    children: React.ReactNode;
    align?: 'left' | 'right';
  }> = ({ field, children, align = 'left' }) => {
    const active = sortField === field;
    const justify = align === 'right' ? 'justify-end' : 'justify-start';
    return (
      <button
        type="button"
        onClick={() => handleSort(field)}
        className={`inline-flex w-full items-center gap-1 ${justify} font-semibold text-[var(--color-text-primary)] hover:text-[var(--color-accent)]`}
      >
        {children}
        {active &&
          (sortOrder === 'asc' ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          ))}
      </button>
    );
  };

  const thClass =
    'px-3 py-2 text-xs font-semibold text-[var(--color-text-primary)] border-b border-[var(--color-border)]';
  const tdClass = 'px-3 py-2 border-b border-[var(--color-border)]';

  if (!data || data.length === 0) {
    return (
      <p className="py-2 text-sm text-[var(--color-text-secondary)]">
        No workspace attribution data available
      </p>
    );
  }

  return (
    <div>
      {/* Summary Row */}
      <div className="mb-4 flex gap-6">
        <span className="text-sm text-[var(--color-text-secondary)]">
          <strong>{data.length}</strong> workspaces
        </span>
        <span className="text-sm text-[var(--color-text-secondary)]">
          <strong>{formatStorage(totals.totalStorage)}</strong> total storage
          <UnmeasuredSizeTag count={totals.totalNotMeasured} />
        </span>
        <span className="text-sm text-[var(--color-text-secondary)]">
          <strong>{totals.totalLakehouses}</strong> lakehouses
        </span>
        <span className="text-sm text-[var(--color-text-secondary)]">
          <strong>{totals.totalWarehouses}</strong> warehouses
        </span>
      </div>

      <Card variant="default" padding="none">
      <DualScrollTable>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className={`${thClass} text-left`}>
                <SortHeader field="workspace_name">Workspace</SortHeader>
              </th>
              <th className={`${thClass} text-left`}>
                <SortHeader field="client_name">Client</SortHeader>
              </th>
              <th className={`${thClass} text-right`}>
                <SortHeader field="storage_gb" align="right">Storage</SortHeader>
              </th>
              <th className={`${thClass} text-right`} style={{ minWidth: 150 }}>
                <SortHeader field="storage_percent" align="right">% of Capacity</SortHeader>
              </th>
              <th className={`${thClass} text-right`}>
                <SortHeader field="lakehouse_count" align="right">Lakehouses</SortHeader>
              </th>
              <th className={`${thClass} text-right`}>
                <SortHeader field="warehouse_count" align="right">Warehouses</SortHeader>
              </th>
              <th className={`${thClass} text-left`}>
                <SortHeader field="last_activity_date">Last Activity</SortHeader>
              </th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((row) => (
              <tr key={row.workspace_id} className="hover:bg-[var(--color-bg-subtle)]">
                <td className={tdClass}>
                  <AppTooltip title={`ID: ${row.workspace_id}`}>
                    <span className="text-sm font-medium text-[var(--color-text-primary)]">
                      {row.workspace_name}
                    </span>
                  </AppTooltip>
                </td>
                <td className={tdClass}>
                  {row.client_name ? (
                    <Badge variant="default" isOutlined>
                      {row.client_name}
                    </Badge>
                  ) : (
                    <span className="text-sm text-[var(--color-text-secondary)]">—</span>
                  )}
                </td>
                <td className={`${tdClass} text-right`}>
                  <span className="text-sm text-[var(--color-text-primary)]">
                    {formatStorage(row.storage_gb)}
                  </span>
                  <UnmeasuredSizeTag count={row.sizeNotMeasuredCount} />
                </td>
                <td className={`${tdClass} text-right`}>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 flex-grow overflow-hidden rounded bg-[var(--color-bg-subtle)]" style={{ width: 60 }}>
                      <div
                        className="h-full rounded"
                        style={{
                          width: `${Math.min(row.storage_percent, 100)}%`,
                          backgroundColor:
                            row.storage_percent > 20
                              ? 'var(--color-status-warning)'
                              : 'var(--color-accent)',
                        }}
                      />
                    </div>
                    <span className="text-sm text-[var(--color-text-primary)]" style={{ minWidth: 45, textAlign: 'right' }}>
                      {row.storage_percent.toFixed(1)}%
                    </span>
                  </div>
                </td>
                <td className={`${tdClass} text-right`}>
                  <span className="text-sm text-[var(--color-text-primary)]">{row.lakehouse_count}</span>
                </td>
                <td className={`${tdClass} text-right`}>
                  <span className="text-sm text-[var(--color-text-primary)]">{row.warehouse_count}</span>
                </td>
                <td className={tdClass}>
                  <span
                    className={`text-sm ${
                      row.last_activity_date
                        ? 'text-[var(--color-text-primary)]'
                        : 'text-[var(--color-text-secondary)]'
                    }`}
                  >
                    {formatDate(row.last_activity_date)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DualScrollTable>
      <PaginationControls
        page={page}
        pageSize={pageSize}
        total={total}
        totalPages={totalPages}
        start={start}
        shown={pageItems.length}
        onPage={setPage}
        onPageSize={changeSize}
      />
      </Card>
    </div>
  );
};

export default WorkspaceAttributionTable;
